import { createContext, KeyboardEvent, useState } from 'react'
import { FormProvider } from 'react-hook-form'
import { useTranslation } from 'react-i18next'
import _ from 'lodash'
import { Button, FormGenerator } from '@microservices/wiskey-react-components'
import { Box, CircularProgress, Grid, Typography } from '@mui/material'

import { useDiagramsCreateOrEdit } from '@pages/DiagramsCreateOrEdit/hooks/useDiagramsCreateOrEdit'
import { PageContextType } from '@pages/DiagramsCreateOrEdit/types'

import { PageContentLayout } from '@layouts/PageContentLayout'
import { EditConfigMenu } from '@components/ConfigEditMenu'
import { EditConfigScriptDialog } from '@components/EditConfigScriptDialog'
import { GeneralButtons } from '@components/GeneralButtons'
import { NewModel } from '@components/NewModel'
import { SwitchTable } from '@components/SwitchTable'
import { Tab, TabPanel, Tabs } from '@components/Tabs'

import { EditConfigProvider } from '@providers/EditConfigProvider'

import { DIAGRAMS_TABS, ENTITY_CONFIG_TYPE, HOT_KEY, MODEL_TYPE } from '@constants'

import { OBJECT_TYPE } from '../../types/api/barChart.api'

export const PageContext = createContext<PageContextType>({} as PageContextType)

export const DiagramsCreateOrEdit = () => {
  const { t } = useTranslation()

  const { state, handlers, inputs, data } = useDiagramsCreateOrEdit()

  const {
    editColumnData,
    methods,
    currentTab,
    showAddDialog,
    globalDirty,
    isLoadingCreate,
    maxSeriesLength,
    isLoading,
  } = state

  const { watch } = methods

  const { series, columns } = data

  const watchId = watch('id')

  const {
    handleSaveDiagram,
    handleChangeTab,
    handleAddSeries,
    handleCancelDiagram,
    handleDeleteColumn,
    handleEditColumn,
    handleDragEnd,
    handleDeleteSeries,
    updateSeriesRow,
    onShowDialog,
    onCloseModal,
    updateDiagramData,
    handleChangeSeries,
  } = handlers

  const { timelineInputs, renderViewInputs } = inputs

  const contextValue = {
    showDialog: showAddDialog,
    objects: state.objectsOptions,
    modalType: MODEL_TYPE.BAR_CHART_COLUMN,
    onCloseModal,
    series,
    updateDiagramData,
    diagramId: watchId,
    editColumnData,
  }

  return (
    <PageContext.Provider value={contextValue}>
      <EditConfigProvider entityType={ENTITY_CONFIG_TYPE.DIAGRAMS}>
        <PageContentLayout sx={{ p: 1, display: 'flex', flexDirection: 'column' }}>
          <EditConfigScriptDialog />
          <Grid container alignItems={'center'} justifyContent={'space-between'} py={0.5}>
            <Typography mb={1} variant='h5'>
              {t(`diagramsCreateOrEdit.header`)}
            </Typography>
            {watchId && <EditConfigMenu configId={watchId} />}
          </Grid>
          {isLoading ? (
            <Box sx={{ flex: 1, alignItems: 'center', display: 'flex', justifyContent: 'center' }}>
              <CircularProgress />
            </Box>
          ) : (
            <FormProvider {...methods}>
              <NewModel />
              {Boolean(watchId) && (
                <>
                  <Grid container alignItems={'center'} justifyContent={'space-between'} pt={2}>
                    <Tabs
                      allowScrollButtonsMobile
                      scrollButtons
                      value={currentTab}
                      variant='scrollable'
                      onChange={handleChangeTab}
                    >
                      <Tab
                        label={t('diagramsCreateOrEdit.tabs.axisX')}
                        value={DIAGRAMS_TABS.AXISX}
                      />
                      <Tab
                        label={t('diagramsCreateOrEdit.tabs.columns')}
                        value={DIAGRAMS_TABS.COLUMNS}
                      />
                      <Tab label={t('diagramsCreateOrEdit.tabs.view')} value={DIAGRAMS_TABS.VIEW} />
                    </Tabs>
                    {currentTab === DIAGRAMS_TABS.COLUMNS && (
                      <Button variant={'contained'} onClick={onShowDialog}>
                        {t('diagramsCreateOrEdit.columns.addColumn')}
                      </Button>
                    )}
                  </Grid>

                  <TabPanel index={DIAGRAMS_TABS.AXISX} value={currentTab}>
                    <FormGenerator inputs={timelineInputs} sx={{ pt: 1 }} />
                    <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                      <Typography mb={1} variant='h5'>
                        {t(`diagramsCreateOrEdit.axisX.series`)}
                      </Typography>
                      <Box display={'flex'} gap={1}>
                        <Button variant={'contained'} onClick={handleChangeSeries}>
                          {'Generate series'}
                        </Button>
                        <Button variant={'contained'} onClick={handleAddSeries}>
                          {t('diagramsCreateOrEdit.axisX.addSeries')}
                        </Button>
                      </Box>
                    </Box>
                    <SwitchTable
                      disablePagination
                      disableSort
                      disabledEditActionColumn
                      showActionsColumn
                      useDragAndDrop
                      rows={series}
                      updateRow={updateSeriesRow}
                      columns={[
                        {
                          accessorKey: 'title',
                          header: t('diagramsCreateOrEdit.axisX.title'),
                          meta: { flex: 1 },
                        },
                        {
                          accessorKey: 'dateRange.from',
                          header: t('diagramsCreateOrEdit.axisX.from'),
                          meta: { flex: 1 },
                          cell: ({ cell }) => (
                            <InputCell
                              cell={cell}
                              column={'dateRange.from'}
                              updateData={updateSeriesRow}
                            />
                          ),
                        },
                        {
                          accessorKey: 'dateRange.till',
                          header: t('diagramsCreateOrEdit.axisX.to'),
                          meta: { flex: 1 },
                          cell: ({ cell }) => (
                            <InputCell
                              cell={cell}
                              column={'dateRange.till'}
                              updateData={updateSeriesRow}
                            />
                          ),
                        },
                      ]}
                      containerSx={{
                        '.MuiTableBody-root > div': {
                          height: '400px !important',
                        },
                      }}
                      onDelete={handleDeleteSeries}
                      onDropChange={newArray => handleDragEnd(newArray, OBJECT_TYPE.SERIES)}
                    />
                  </TabPanel>
                  <TabPanel index={DIAGRAMS_TABS.COLUMNS} value={currentTab}>
                    <SwitchTable
                      disablePagination
                      disableSort
                      showActionsColumn
                      useDragAndDrop
                      rows={columns}
                      showDialog={showAddDialog}
                      type={'bar_chart_column'}
                      columns={[
                        {
                          accessorKey: 'code',
                          header: t('diagramsCreateOrEdit.columns.code'),
                          meta: { flex: 1 },
                        },
                        {
                          accessorKey: 'title',
                          header: t('diagramsCreateOrEdit.columns.title'),
                          meta: { flex: 1 },
                        },
                        {
                          accessorKey: 'objectTitle',
                          header: t('diagramsCreateOrEdit.columns.object'),
                          meta: { flex: 1 },
                        },
                        {
                          accessorKey: 'color',
                          header: t('diagramsCreateOrEdit.columns.color'),
                          meta: { flex: 1 },
                          cell: ({ cell }) => (
                            <Box
                              sx={theme => ({
                                width: '31px',
                                height: '31px',
                                border: '1px solid',
                                borderColor: theme.palette.grey[700],
                                backgroundColor: cell.row.original.color,
                              })}
                            />
                          ),
                        },
                      ]}
                      containerSx={{
                        '.MuiTableBody-root > div': {
                          height: '400px !important',
                        },
                      }}
                      onDelete={handleDeleteColumn}
                      onDropChange={newArray => handleDragEnd(newArray, OBJECT_TYPE.COLUMN)}
                      onEdit={params => handleEditColumn(params.row.original.id)}
                      onShowDialog={showAddDialog}
                    />
                  </TabPanel>
                  <TabPanel index={DIAGRAMS_TABS.VIEW} value={currentTab}>
                    {renderViewInputs()}
                  </TabPanel>
                </>
              )}

              <Grid container gap={1} justifyContent={'flex-end'} pb={1} pt={2} width={'100%'}>
                <GeneralButtons
                  cancelText={t('diagramsCreateOrEdit.cancelButton')}
                  isLoading={isLoadingCreate || isLoading}
                  saveDisabled={!globalDirty || isLoadingCreate}
                  saveText={t('diagramsCreateOrEdit.saveButton')}
                  onCancel={handleCancelDiagram}
                  onSave={handleSaveDiagram}
                />
              </Grid>
            </FormProvider>
          )}
        </PageContentLayout>
      </EditConfigProvider>
    </PageContext.Provider>
  )
}

type InputCellType = {
  cell: any
  column: string
  updateData: (data: any) => void
}

const InputCell = ({ cell, column, updateData }: InputCellType) => {
  const row = cell.row.original
  const prevValue = _.get(row, column)
  const [value, setValue] = useState(prevValue)

  const updateRowData = (_value: unknown) => updateData(_.set(row, column, _value || 0))
  const onBlur = () => updateRowData(prevValue)

  const onKeyUp = (event: KeyboardEvent<HTMLInputElement>) => {
    if (event.key === HOT_KEY.ENTER) {
      updateRowData(value)
    }

    if (event.key === HOT_KEY.ESCAPE) {
      updateRowData(prevValue)
    }
  }

  return (
    <input
      style={{ width: '100%' }}
      value={value as string}
      onBlur={onBlur}
      onKeyUp={onKeyUp}
      onChange={event => {
        const regexp = /^\d{0,2}$/
        if (regexp.test(event.target.value)) {
          setValue(event.target.value)
        }
      }}
    />
  )
}
